import { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Box } from "@mui/system";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useRecoilValue } from "recoil";
import { Chip, Divider, useMediaQuery } from "@mui/material";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";

import Events from "./events/Events";
import AINote from "../ai-note/AINote";
import Options from "./options/Options";
import Skeleton from "../skeleton/Skeleton";
import ChatInput from "../chat-input/ChatInput";
import ShortScores from "./short-scores/ShortScores";
import MarketMovers from "./market-movers/MarketMovers";
import { LatestMorningNote } from "@/models/morning-note";
import DataHighlights from "./data-highlights/DataHighlights";
import TradingSignals from "./trading-signals/TradingSignals";
import SubscriptionBanner from "../subscription-banner/SubscriptionBanner";

import { getDataHightlightsStocks } from "@/controllers/data-highlights";
import { getStockMovesFallers, getStockMovesRisers } from "@/controllers/stock-moves";
import { getGeneralNote, getMorningNoteData, GeneralNoteResponse } from "@/controllers/morning-note";

import { isOrtexApp } from "@/lib/utils";
import { ModuleContext } from "@/lib/Context";
import { blackLighterState, blackState, dividerState, ortexColorState, textColorState, themeState, whiteDarkerState, whiteMediumState } from "@/lib/store";

import styles from "./MorningNote.module.scss";
import { brighten } from "@/lib/ortex-charts/color";

interface Props {
    note?: LatestMorningNote;
    staff?: boolean;
    setFilters?: (filters: JSX.Element) => void;
    admin?: boolean;
    openEditor?: boolean;
    setOpenEditor?: (value: boolean) => void;
    setNote?: (value: any) => void;
}

const MorningNote = ({ note }: Props) => {
    const { obfuscated, setObfuscated } = useContext(ModuleContext);

    const textColor = useRecoilValue(textColorState);
    const theme = useRecoilValue(themeState);
    const blackLighter = useRecoilValue(blackLighterState);
    const whiteDarker = useRecoilValue(whiteDarkerState);
    const whiteMedium = useRecoilValue(whiteMediumState);
    const black = useRecoilValue(blackState);
    const ortexColor = useRecoilValue(ortexColorState);

    const [generalNote, setGeneralNote] = useState<string | null>(null);
    const [generalNoteObfuscated, setGeneralNoteObfuscated] = useState(false);
    const [noteDate, setNoteDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

    const [stockMovers, setStockMovers] = useState<any[]>([]);
    const [tradingSignals, setTradingSignals] = useState<any[]>([]);
    const [events, setEvents] = useState<any[]>(null);
    const [shortScores, setShortScores] = useState<any[]>([]);
    const [options, setOptions] = useState<any[]>([]);
    const [highlightsData, setHighlightsData] = useState<any[]>([]);
    const [highlightsDataObfuscated, setHighlightsDataObfuscated] = useState(false);

    const isLaptopOrGreater = useMediaQuery("(min-width: 1279px)");
    const is4k = useMediaQuery("(min-width: 3800px)");
    const isDesktop = useMediaQuery("(min-width: 1919px)");
    const isMobile = useMediaQuery("(max-width: 769px)");

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingStocks, setIsLoadingStocks] = useState(true);

    const dividerColor = useRecoilValue(dividerState);

    const isAdminMode = !!note;
    const displayNote = note?.note || generalNote;
    const isPublished = (note?.published ?? !!generalNote) || generalNoteObfuscated;

    useEffect(() => {
        if (!note) {
            Promise.allSettled([getDataHightlightsStocks(), getGeneralNote()])
                .then((results) => {
                    const [highlightsResult, generalNoteResult] = results;

                    if (highlightsResult.status === "fulfilled") {
                        const { data, obfuscated } = highlightsResult.value;
                        setHighlightsData(data);
                        setHighlightsDataObfuscated(obfuscated);
                    } else {
                        console.error("Error fetching highlights data:", highlightsResult.reason);
                    }

                    if (generalNoteResult.status === "fulfilled") {
                        const value = generalNoteResult.value;
                        // ``getGeneralNote`` historically returned EITHER a
                        // plain string (the body) when the caller was
                        // entitled, OR a ``{response, obfuscated}`` object
                        // when premium-gated. After the move to the MI
                        // commentary endpoint it now always returns the
                        // object shape — ``response`` carries the body and
                        // ``obfuscated`` is ``false`` for free callers. We
                        // still handle the legacy string shape defensively
                        // so a partial deploy / cached bundle doesn't
                        // blank the section out.
                        if (typeof value === "object" && value !== null && "obfuscated" in value) {
                            const typed = value as GeneralNoteResponse;
                            setGeneralNoteObfuscated(!!typed.obfuscated);
                            // When ``obfuscated`` is true we want to render
                            // the SubscriptionBanner, so the body intentionally
                            // stays null. Otherwise pull the body out of the
                            // wrapper — the previous code unconditionally
                            // dropped it which blanked Market Commentary on
                            // every load post-cutover.
                            setGeneralNote(typed.obfuscated ? null : typed.response ?? null);
                        } else {
                            setGeneralNoteObfuscated(false);
                            setGeneralNote(typeof value === "string" ? value : null);
                        }
                    } else {
                        console.error("Error fetching general note data:", generalNoteResult.reason);
                        setGeneralNote("No general note found. Please try again later.");
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });

            Promise.all([getStockMovesRisers({ page_size: 5, page: 1, universe_id: 100, filter_options: {} }), getStockMovesFallers({ page_size: 5, page: 1, universe_id: 100, filter_options: {} })])
                .then((res) => {
                    const risers = res[0].rows.map((row, i) => {
                        return { path: `${row.exchangeSymbol}/${row.ticker}`, ticker: row.ticker, priceChange: row.priceChange, obfuscated: res[0].obfuscation?.obfuscated && i >= res[0].obfuscation?.doNotObfuscateRows ? true : false };
                    });
                    const fallers = res[1].rows.map((row, i) => {
                        return { path: `${row.exchangeSymbol}/${row.ticker}`, ticker: row.ticker, priceChange: row.priceChange, obfuscated: res[1].obfuscation?.obfuscated && i >= res[1].obfuscation?.doNotObfuscateRows ? true : false };
                    });

                    setStockMovers([...risers, ...fallers]);
                })
                .finally(() => {
                    setIsLoadingStocks(false);
                });

            getMorningNoteData().then((data) => {
                setTradingSignals(data.trading_signals);
                setEvents(data.events);
                setShortScores(data.short_scores);
                setOptions(data.options);
            });
        } else {
            setIsLoading(false);
            if (note.note_date) {
                setNoteDate(typeof note.note_date === "string" ? note.note_date : dayjs(note.note_date).format("YYYY-MM-DD"));
            }
            if (note.obfuscated !== undefined) {
                setObfuscated && setObfuscated(note.obfuscated);
            }
        }
    }, [note, setObfuscated]);

    return (
        <div className={styles.MorningNote}>
            <style jsx global>{`
                .${styles.date} {
                    color: ${theme === "dark" ? whiteDarker : blackLighter};
                }

                .${styles.editor} h1 {
                    color: "${theme === "dark" ? whiteDarker : blackLighter}";
                }

                .${styles.editor} h2 {
                    border-top: 1px solid ${theme === "dark" ? whiteMedium : (brighten(whiteDarker, -0.05))};
                    color: "${theme === "dark" ? whiteDarker : blackLighter}";
                }

                .${styles.editor} p {
                    color: ${theme === "dark" ? whiteDarker : black};
                }
                a {
                    color: ${ortexColor};
                }
                div#note p {
                    margin-bottom: 0.5rem;
                }
                div#note h1 {
                    font-size: 26px;
                    margin-bottom: 10px;
                }
            `}</style>

            {isLoading ? (
                <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
            ) : (
                <Box sx={{ height: "100%", overflowY: "hidden", position: "relative" }}>
                    {isPublished && (displayNote || generalNoteObfuscated) && (
                        <Box sx={{ height: "100%", overflowY: "auto", paddingBottom: 10 }}>
                            {!isAdminMode && (
                                <Box id="note" sx={{ marginY: 1 }}>
                                    {highlightsData.length ? (
                                        <>
                                            <Box sx={{ height: "32px", marginY: 1, position: "sticky" }}>
                                                <Divider
                                                    sx={{
                                                        "&::before, &::after": {
                                                            borderColor: dividerColor,
                                                        },
                                                    }}
                                                >
                                                    <Chip label={dayjs(noteDate).format("dddd") + "'s Data Highlights"} sx={{ backgroundColor: dividerColor, color: textColor }} />
                                                </Divider>
                                            </Box>

                                            <Box sx={{ height: "calc(100% - 32px)", px: 1 }}>
                                                <DataHighlights highlightsData={highlightsData} obfuscated={highlightsDataObfuscated} />
                                            </Box>
                                        </>
                                    ) : null}
                                </Box>
                            )}
                            <Box id="note" sx={{ marginY: 1 }}>
                                <Box sx={{ height: "32px", marginY: 1, position: "sticky" }}>
                                    <Divider
                                        sx={{
                                            "&::before, &::after": {
                                                borderColor: dividerColor,
                                            },
                                        }}
                                    >
                                        <Chip label={"Market News"} sx={{ backgroundColor: dividerColor, color: textColor }} />
                                    </Divider>
                                </Box>
                                <Box sx={{ height: "calc(100% - 32px)", px: 1 }}>
                                    {isAdminMode ? (
                                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} linkTarget={isOrtexApp() ? "_self" : "_blank"}>
                                            {displayNote}
                                        </ReactMarkdown>
                                    ) : generalNoteObfuscated ? (
                                        <SubscriptionBanner />
                                    ) : (
                                        <AINote content={displayNote} isLoading={isLoading} />
                                    )}
                                </Box>
                            </Box>
                            {!isAdminMode && (
                                <>
                                    <MarketMovers stockMovers={stockMovers} is4k={is4k} isDesktop={isDesktop} isLoadingStocks={isLoadingStocks} />
                                    {tradingSignals?.length ? <TradingSignals tradingSignals={tradingSignals} /> : null}
                                    {events?.length ? <Events events={events} /> : null}
                                    {shortScores?.length ? <ShortScores shortScores={shortScores} is4k={is4k} isDesktop={isDesktop} isLaptopOrGreater={isLaptopOrGreater} isMobile={isMobile} /> : null}
                                    {options?.length ? <Options options={options} is4k={is4k} isDesktop={isDesktop} /> : null}
                                </>
                            )}
                        </Box>
                    )}
                    {!generalNoteObfuscated && <ChatInput placeholder="Ask more about market commentary..." startingModel="general_note" />}
                </Box>
            )}
        </div>
    );
};

export default MorningNote;
